import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

export function QuestionnaireCompleted({ isActive, closeModal }) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const handleClose = () => {
        queryClient.invalidateQueries(['questionnaires']);
        navigate('/vendors');
        closeModal();
    };

    return (
        <AnimatePresence>
            {isActive && (
                <motion.div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{
                        zIndex: 1000,
                        backdropFilter: 'blur(8px)'
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    onClick={handleClose}
                >
                    <motion.div
                        className="bg-white rounded-4 shadow-lg position-relative overflow-hidden"
                        style={{
                            maxWidth: '500px',
                            width: '90%'
                        }}
                        initial={{ scale: 0.8, y: -50, opacity: 0 }}
                        animate={{ scale: 1, y: 0, opacity: 1 }}
                        exit={{ scale: 0.8, y: 50, opacity: 0 }}
                        transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 25
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Gradient Top Border */}
                        <div
                            style={{
                                height: '6px',
                                background: 'var(--accent)'
                            }}
                        />

                        {/* Success Icon Section */}
                        <div className="text-center pt-5 pb-4 px-4">
                            {/* Animated Success Icon */}
                            <motion.div
                                className="d-inline-flex align-items-center justify-content-center mb-4 position-relative"
                                style={{
                                    width: '100px',
                                    height: '100px'
                                }}
                                initial={{ scale: 0, rotate: -180 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{
                                    delay: 0.2,
                                    type: "spring",
                                    stiffness: 200,
                                    damping: 15
                                }}
                            >
                                {/* Outer Pulse Ring */}
                                <motion.div
                                    className="position-absolute"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        borderRadius: '50%',
                                        backgroundColor: 'rgba(199, 29, 150, 0.2)'
                                    }}
                                    animate={{
                                        scale: [1, 1.2, 1],
                                        opacity: [0.3, 0.1, 0.3]
                                    }}
                                    transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        ease: "easeInOut"
                                    }}
                                />

                                {/* Main Circle */}
                                <div
                                    className="position-relative d-flex align-items-center justify-content-center"
                                    style={{
                                        width: '85px',
                                        height: '85px',
                                        borderRadius: '50%',
                                        background: 'var(--accent)',
                                        boxShadow: '0 10px 30px rgba(199, 29, 150, 0.4)'
                                    }}
                                >
                                    <motion.i
                                        className="fas fa-check"
                                        style={{
                                            fontSize: '3rem',
                                            color: 'white'
                                        }}
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{
                                            delay: 0.4,
                                            type: "spring",
                                            stiffness: 300
                                        }}
                                    />
                                </div>
                            </motion.div>

                            {/* Title */}
                            <motion.h3
                                className="mb-3"
                                style={{
                                    color: 'var(--ink)',
                                    fontWeight: '700',
                                    fontSize: '1.75rem',
                                    letterSpacing: '-0.5px'
                                }}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                            >
                                Successfully Submitted!
                            </motion.h3>

                            {/* Description */}
                            <motion.p
                                className="mb-0"
                                style={{
                                    color: 'var(--ink-soft)',
                                    fontSize: '1rem',
                                    lineHeight: '1.7',
                                    maxWidth: '400px',
                                    margin: '0 auto'
                                }}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4 }}
                            >
                                Thank you for completing the questionnaire. It has been submitted for review. Our team will contact you if clarification is required.
                            </motion.p>
                        </div>

                        {/* Info Box */}
                        <motion.div
                            className="mx-4 mb-4 p-3 rounded-3"
                            style={{
                                backgroundColor: 'var(--info-soft)',
                                border: '1px solid var(--hairline)',
                                borderLeft: '4px solid var(--ink)'
                            }}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.5 }}
                        >
                            <div className="d-flex align-items-start">
                                <i
                                    className="fas fa-info-circle me-3 mt-1"
                                    style={{ color: 'var(--ink)', fontSize: '1rem' }}
                                ></i>
                                <small style={{ color: 'var(--ink)', lineHeight: '1.6' }}>
                                    <strong className="d-block mb-1">What happens next?</strong>
                                    Your responses are being reviewed by our team. You'll receive a notification once the review is complete.
                                </small>
                            </div>
                        </motion.div>

                        {/* Footer */}
                        <motion.div
                            className="p-4 border-top text-center"
                            style={{
                                backgroundColor: 'var(--surface-sunken)',
                                borderColor: 'var(--hairline)'
                            }}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6 }}
                        >
                            <motion.button
                                className="btn px-5 py-2 fw-semibold shadow-sm"
                                style={{
                                    borderRadius: '8px',
                                    backgroundColor: 'var(--accent)',
                                    color: 'white',
                                    border: 'none',
                                    minWidth: '160px'
                                }}
                                onClick={handleClose}
                                whileHover={{
                                    scale: 1.02,
                                    backgroundColor: 'var(--accent-strong)',
                                    boxShadow: '0 6px 20px rgba(199, 29, 150, 0.35)'
                                }}
                                whileTap={{ scale: 0.98 }}
                                transition={{ type: "spring", stiffness: 400, damping: 17 }}
                            >
                                <i className="fas fa-check-double me-2"></i>
                                Done
                            </motion.button>

                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.8 }}
                            >
                                <small
                                    className="d-block mt-3 text-muted"
                                    style={{ fontSize: '0.8rem' }}
                                >
                                    You can safely close this window
                                </small>
                            </motion.div>
                        </motion.div>

                        {/* Confetti Animation */}
                        <motion.div
                            className="position-absolute"
                            style={{
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                pointerEvents: 'none'
                            }}
                            initial={{ scale: 0 }}
                            animate={{ scale: [0, 1.5, 0] }}
                            transition={{
                                delay: 0.2,
                                duration: 0.8,
                                ease: "easeOut"
                            }}
                        >
                            {/* Sparkle elements */}
                            {[...Array(8)].map((_, i) => (
                                <motion.div
                                    key={i}
                                    className="position-absolute"
                                    style={{
                                        width: '4px',
                                        height: '4px',
                                        borderRadius: '50%',
                                        backgroundColor: 'var(--accent)'
                                    }}
                                    initial={{
                                        x: 0,
                                        y: 0,
                                        opacity: 0
                                    }}
                                    animate={{
                                        x: Math.cos((i / 8) * Math.PI * 2) * 80,
                                        y: Math.sin((i / 8) * Math.PI * 2) * 80,
                                        opacity: [0, 1, 0]
                                    }}
                                    transition={{
                                        delay: 0.3,
                                        duration: 0.8,
                                        ease: "easeOut"
                                    }}
                                />
                            ))}
                        </motion.div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}